/**
 * Logger configuration options
 */
export interface LoggerConfig {
  level?: "trace" | "debug" | "info" | "warn" | "error" | "fatal";
  enabled?: boolean;
  name?: string;
  prettyPrint?: boolean;
  redact?: string[];
}

/**
 * Logger interface for consistent logging across data layer adapters
 */
export interface Logger {
  trace: (message: string, ...args: any[]) => void;
  debug: (message: string, ...args: any[]) => void;
  info: (message: string, ...args: any[]) => void;
  warn: (message: string, ...args: any[]) => void;
  error: (message: string, error?: Error, ...args: any[]) => void;
  fatal: (message: string, error?: Error, ...args: any[]) => void;
  child: (bindings: Record<string, any>) => Logger;
}

/**
 * Log levels with numeric values for comparison
 */
const LOG_LEVELS = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  fatal: 60,
} as const;

/**
 * No-op logger for when logging is disabled
 */
class NoOpLogger implements Logger {
  trace() {}
  debug() {}
  info() {}
  warn() {}
  error() {}
  fatal() {}
  child(): Logger {
    return new NoOpLogger();
  }
}

/**
 * Simple universal logger that works in all environments
 */
class UniversalLogger implements Logger {
  private config: Required<LoggerConfig>;
  private bindings: Record<string, any>;

  constructor(
    config: Required<LoggerConfig>,
    bindings: Record<string, any> = {}
  ) {
    this.config = config;
    this.bindings = bindings;
  }

  private shouldLog(level: keyof typeof LOG_LEVELS): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.level];
  }

  private formatMessage(
    level: string,
    message: string,
    args: any[],
    error?: Error
  ): string {
    const timestamp = new Date().toISOString();
    const name = this.config.name;

    let logMessage = this.config.prettyPrint
      ? `[${timestamp}] ${level.toUpperCase()} [${name}]: ${message}`
      : JSON.stringify({
          timestamp,
          level,
          name,
          message,
          ...this.bindings,
          ...(args.length > 0 && { args }),
          ...(error && {
            error: { message: error.message, stack: error.stack },
          }),
        });

    if (this.config.prettyPrint && args.length > 0) {
      logMessage += ` ${args
        .map((arg) =>
          typeof arg === "object" ? JSON.stringify(arg) : String(arg)
        )
        .join(" ")}`;
    }

    if (this.config.prettyPrint && error) {
      logMessage += ` Error: ${error.message}`;
      if (error.stack) {
        logMessage += `\n${error.stack}`;
      }
    }

    return this.redactSensitiveData(logMessage);
  }

  private redactSensitiveData(message: string): string {
    let redactedMessage = message;
    for (const field of this.config.redact) {
      const regex = new RegExp(`"${field}"\\s*:\\s*"[^"]*"`, "gi");
      redactedMessage = redactedMessage.replace(
        regex,
        `"${field}":"[REDACTED]"`
      );
    }
    return redactedMessage;
  }

  private log(
    level: keyof typeof LOG_LEVELS,
    message: string,
    args: any[],
    error?: Error
  ): void {
    if (!this.shouldLog(level)) return;

    const formattedMessage = this.formatMessage(level, message, args, error);

    // Use appropriate console method based on level
    switch (level) {
      case "trace":
        console.trace(formattedMessage);
        break;
      case "debug":
        console.debug(formattedMessage);
        break;
      case "info":
        console.info(formattedMessage);
        break;
      case "warn":
        console.warn(formattedMessage);
        break;
      case "error":
      case "fatal":
        console.error(formattedMessage);
        break;
    }
  }

  trace(message: string, ...args: any[]): void {
    this.log("trace", message, args);
  }

  debug(message: string, ...args: any[]): void {
    this.log("debug", message, args);
  }

  info(message: string, ...args: any[]): void {
    this.log("info", message, args);
  }

  warn(message: string, ...args: any[]): void {
    this.log("warn", message, args);
  }

  error(message: string, error?: Error, ...args: any[]): void {
    this.log("error", message, args, error);
  }

  fatal(message: string, error?: Error, ...args: any[]): void {
    this.log("fatal", message, args, error);
  }

  child(bindings: Record<string, any>): Logger {
    return new UniversalLogger(this.config, { ...this.bindings, ...bindings });
  }
}

/**
 * Create a logger instance
 */
export function createLogger(config: LoggerConfig = {}): Logger {
  const fullConfig: Required<LoggerConfig> = {
    level: config.level || "info",
    enabled: config.enabled !== false,
    name: config.name || "@shopkit/data-layer",
    prettyPrint: config.prettyPrint || false,
    redact: config.redact || ["password", "token", "apiKey", "secret"],
  };

  // Return no-op logger if disabled
  if (!fullConfig.enabled) {
    return new NoOpLogger();
  }

  return new UniversalLogger(fullConfig);
}

/**
 * Default logger instance for data layer
 * Note: This is disabled by default. Enable it by passing config to createCommerceClient
 */
export const logger = createLogger({
  enabled: false,
  level: "info",
  prettyPrint: false,
});

/**
 * Create adapter-specific logger
 */
export function createAdapterLogger(
  adapterName: string,
  config: LoggerConfig = {}
): Logger {
  const baseLogger = createLogger(config);
  return baseLogger.child({ adapter: adapterName });
}

/**
 * Create service-specific logger
 */
export function createServiceLogger(
  serviceName: string,
  config: LoggerConfig = {}
): Logger {
  const baseLogger = createLogger(config);
  return baseLogger.child({ service: serviceName });
}

/**
 * Create domain-specific logger (e.g., product, cart, customer)
 */
export function createDomainLogger(
  domain: string,
  adapter: string,
  config: LoggerConfig = {}
): Logger {
  const baseLogger = createLogger(config);
  return baseLogger.child({ domain, adapter });
}
